import { computed, ref, onMounted } from 'vue'
import { ProcessingVideo, ProcessingVideoStatus } from '@/services/videos/types'
import { useI18n } from 'vue-i18n'
import type { PostVideoError, SuccessfulVideoInfo } from '@/interfaces'
import { useStore } from 'vuex'
import { useProcessingVideosStorage } from '@/composables/useProcessingVideosStorage/useProcessingVideosStorage'
import { useLocalStorage } from '@vueuse/core'
import { PROCESSING_PANEL_IS_COLLAPSED_KEY } from '@/constants'

const isPanelOpen = ref(false)
const isCollapsed = useLocalStorage(PROCESSING_PANEL_IS_COLLAPSED_KEY, false)
const items = ref<ProcessingVideo[]>([])
const completionSignal = ref(0)

export function useProcessingPanel() {
  const { t } = useI18n()
  const store = useStore()
  const currentUserId = computed(() => store.getters['user/getId']())
  const { clearUserVideos, getVideosForUser, getFailedVideosForUser, removeVideoById, addVideos } = useProcessingVideosStorage()

  const triggerRefresh = () => {
    completionSignal.value++
  }

  const syncItemsFromStorage = () => {
    const userVideos = getVideosForUser(currentUserId.value)
    if (userVideos.length > 0) {
      items.value = userVideos.map((stored) => ({
        video_id: stored.video_id,
        title: stored.title,
        thumbnail_url: stored.thumbnail_url,
        status: stored.status || ProcessingVideoStatus.Pending,
        error: stored.error,
        userId: stored.userId
      }))
    } else {
      items.value = []
    }
  }

  const initializeFromStorage = () => {
    syncItemsFromStorage()
    isPanelOpen.value = items.value.length > 0
  }

  onMounted(() => {
    initializeFromStorage()
  })

  const nonActiveVideos = computed(() => items.value.filter((item) => item.status !== ProcessingVideoStatus.Active))
  const processingCount = computed(() => nonActiveVideos.value.filter((v) => v.status !== ProcessingVideoStatus.Failed).length)
  const failedCount = computed(() => nonActiveVideos.value.filter((v) => v.status === ProcessingVideoStatus.Failed).length)
  const totalCount = computed(() => nonActiveVideos.value.length)
  const canClose = computed(() => totalCount.value > 0 && processingCount.value === 0)
  const isLoading = computed(() => nonActiveVideos.value.length === 0)

  const hasOnlyFailedVideos = computed(() => processingCount.value === 0 && failedCount.value > 0)

  const counterText = computed(() => {
    if (processingCount.value > 0 && failedCount.value > 0) {
      return t('video-processing-header-with-fail', { countText: `${totalCount.value}/${failedCount.value}` })
    } else if (processingCount.value > 0) {
      return t('video-processing-header', { count: totalCount.value })
    } else {
      return t('video-processing-header-failed', { count: failedCount.value })
    }
  })

  const open = () => {
    isPanelOpen.value = true
  }

  const close = () => {
    const allVideosFailed = canClose.value && processingCount.value === 0 && nonActiveVideos.value.length > 0
    if (allVideosFailed) {
      clearUserVideos(currentUserId.value)
    }
    isPanelOpen.value = false
  }

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const updateItems = (newItems: ProcessingVideo[]) => {
    const existingVideoIds = new Set(items.value.map((v) => v.video_id))
    const hasNewItems = newItems.some((v) => !existingVideoIds.has(v.video_id))

    const failedVideosFromStorage = getFailedVideosForUser(currentUserId.value).map((stored) => ({
      video_id: stored.video_id,
      title: stored.title,
      thumbnail_url: stored.thumbnail_url,
      status: ProcessingVideoStatus.Failed,
      error: stored.error,
      userId: stored.userId
    }))

    const allVideos = [...newItems]
    failedVideosFromStorage.forEach((failed) => {
      if (!allVideos.find((v) => v.video_id === failed.video_id)) {
        allVideos.push(failed)
      }
    })

    items.value = allVideos

    if (hasNewItems) {
      isPanelOpen.value = true
      isCollapsed.value = false
    }

    const hasFailedVideos = allVideos.some((v) => v.status === ProcessingVideoStatus.Failed)
    if ((allVideos.length === 0 || nonActiveVideos.value.length === 0) && !hasFailedVideos) {
      isPanelOpen.value = false
    }
  }

  const removeItem = (videoId: string) => {
    items.value = items.value.filter((v) => v.video_id !== videoId)
    removeVideoById(videoId)
    const hasRemainingFailedVideos = items.value.some((v) => v.status === ProcessingVideoStatus.Failed)
    if ((items.value.length === 0 || nonActiveVideos.value.length === 0) && !hasRemainingFailedVideos) {
      isPanelOpen.value = false
    }
  }

  const openPanelWithLoadingSpinner = () => {
    syncItemsFromStorage()
    isPanelOpen.value = true
    isCollapsed.value = false
  }

  const addSuccessfulVideos = (videos: SuccessfulVideoInfo[]) => {
    const videosToStore: ProcessingVideo[] = videos.map((video) => ({
      video_id: video.id,
      title: video.title,
      thumbnail_url: video.thumbnail_url ?? null,
      status: ProcessingVideoStatus.Pending,
      userId: currentUserId.value
    }))
    addVideos(videosToStore)
  }

  const addFailedVideos = (errors: PostVideoError[]) => {
    const failedVideosToStore: ProcessingVideo[] = errors.map((error, index) => {
      const tempId = `failed-${error.video_title}-${index}`
      return {
        video_id: tempId,
        title: error.video_title,
        thumbnail_url: null,
        status: ProcessingVideoStatus.Failed,
        error: error.error,
        userId: currentUserId.value
      }
    })
    addVideos(failedVideosToStore)
  }

  return {
    isPanelOpen,
    isCollapsed,
    isLoading,
    processingVideos: nonActiveVideos,
    processingCount: totalCount,
    counterText,
    hasOnlyFailedVideos,
    canClose,
    open,
    close,
    toggleCollapse,
    updateItems,
    removeItem,
    openPanelWithLoadingSpinner,
    addSuccessfulVideos,
    addFailedVideos,
    completionSignal,
    triggerRefresh
  }
}
